import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidPercentageDecimal,
  useGetDairyManureStorage,
  useGetFootprintingDairyManureStorage,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyManureStorage,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { DAIRY_PRACTICE_SLUG, UploadableManureStorage } from "models";

import { Button, Card, Flex, Form, FormInstance, Icon, IconButton, InputNumber, Option, PageSpin, ProgressBarCardWithContent, Select } from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const manureStoragePlaceholder: UploadableManureStorage = {
  id: undefined,
  manureStorageId: "",
  amountStoredPct: null,
};

export const ManureStorageForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const [appliedManureStorage, setAppliedManureStorage] = useState<UploadableManureStorage[]>([]);
  const [amountStoredGasTankPct, setAmountStoredGasTankPct] = useState<number | null>(null);

  const { data: ManureStorageQuery } = useGetFootprintingDairyManureStorage(primaryDataId);
  const { data: dairyManureStorageQuery, isLoading } = useGetDairyManureStorage();

  const { data: dairyPrimaryDataQuery, isSuccess } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      amountStoredGasTankPct: amountStoredGasTankPct,
      isSubmitted: false,
    },
    primaryDataId
  );

  const selectGroupName = "footprinting-dairy-manure-storage-select";

  const { mutate: mutateDairyManureStorage } = useMutateFootprintingDairyManureStorage(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedManureStorage: appliedManureStorage.filter((m) => !!m.manureStorageId),
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyManureStorage();
        mutateDairyPrimaryData();
      }
    },
    [isLoading, mutateDairyManureStorage, mutateDairyPrimaryData, readOnly]
  );

  // Load manure management data
  useEffect(() => {
    if (ManureStorageQuery) {
      setAppliedManureStorage(
        ManureStorageQuery.length
          ? ManureStorageQuery.map((m) => ({
              id: m.id,
              manureStorageId: m.manureStorageId ?? "",
              amountStoredPct: m.amountStoredPct ?? null,
            }))
          : [{ ...manureStoragePlaceholder }]
      );
    }
  }, [ManureStorageQuery]);

  useEffect(() => {
    if (dairyPrimaryDataQuery && isSuccess) {
      setAmountStoredGasTankPct(dairyPrimaryDataQuery.amountStoredGasTankPct ?? null);

      formRef.current?.setFieldsValue({
        amountStoredGasTankPct: formatFractionInput(dairyPrimaryDataQuery.amountStoredGasTankPct ?? null),
      });

      const fieldsToValidate = (["amountStoredGasTankPct"] as const).some(
        (field) => dairyPrimaryDataQuery[field] !== null && dairyPrimaryDataQuery[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery, isSuccess]);

  // This ensures that all form fields rerender when the non-last manure storage
  // is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedManureStorage.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedManureStorage.length]);

  const totalStoragePercentage = useMemo(
    () => appliedManureStorage.reduce((total, storage) => total + (formatFractionInput(storage.amountStoredPct) || 0), 0),
    [appliedManureStorage]
  );

  const hasLoadedManureStorage = appliedManureStorage.length > 0 && appliedManureStorage[0].id !== undefined;

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyManureStorageForm.title")}</h1>
      <Form ref={formRef}>
        <Card dataTestId="footprinting-dairy-manure-storage-form" className="margin-bottom-100 padding-100">
          <Flex vertical className="padding-bottom-0 padding-top-0 padding-left-100 padding-right-100 margin-bottom-100">
            <h3 className="margin-bottom-0">{t("dairyManureStorageForm.subTitle")}</h3>
            <p className="paragraph-m grey-600-text margin-0 margin-top-25">{t("dairyManureStorageForm.description")}</p>
          </Flex>

          <div className="padding-100">
            {hasLoadedManureStorage && (
              <ProgressBarCardWithContent
                currentValue={totalStoragePercentage}
                title={t("dairyManureStorageForm.progressBar.title")}
                // description={t("dairyManureStorageForm.progressBar.description")} // design decision to comment this out for now.
                errorMessage={t("dairyManureStorageForm.progressBar.errorMessage")}
                successMessage={t("dairyManureStorageForm.progressBar.successMessage")}
                strokeWidth={8}
              />
            )}
          </div>

          {appliedManureStorage.map((manure, index) => {
            const selectGroupWithIndex = `${selectGroupName}-${index}`;
            const updateItem = (item: UploadableManureStorage) => {
              const next = [...appliedManureStorage];
              next[index] = { ...item };
              setAppliedManureStorage(next);
            };

            return (
              <Form.Item key={index} className="padding-100">
                <Flex className="margin-bottom-100" justify="space-between" align="center" vertical={false}>
                  <h5 style={{ flex: 1 }}>{t("dairyManureStorageForm.storageNumber", { count: index + 1 })}</h5>
                  <IconButton
                    type="tertiary"
                    disabled={readOnly}
                    dataTestId={`delete-manure-storage-${index}`}
                    size="small"
                    iconName="tabler/trash"
                    onClick={() => setAppliedManureStorage([...appliedManureStorage].filter((_, i) => i !== index))}
                  />
                </Flex>
                {/* @ts-expect-error need to convert to tsx */}
                <Select
                  showSearch={true}
                  disabled={readOnly}
                  className="margin-bottom-100"
                  name={selectGroupWithIndex}
                  dataTestId={selectGroupWithIndex}
                  label={t("dairyManureStorageForm.question.manureStorageTypeQuestionInput")}
                  rules={[{ required: true, message: false }]}
                  initialValue={dairyManureStorageQuery?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                  value={dairyManureStorageQuery?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                  onChange={(v) => {
                    const matchingPractice = dairyManureStorageQuery?.find((m) => m.name === v);
                    updateItem({
                      ...manure,
                      manureStorageId: matchingPractice ? matchingPractice.id : "",
                    });
                  }}
                  dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                >
                  {(dairyManureStorageQuery ?? []).map((dairyManureStorage) => {
                    // Check if this storage ID is already used in another item
                    const isAlreadyUsed = appliedManureStorage.some(
                      (m) => m.manureStorageId === dairyManureStorage.id && m.manureStorageId !== manure.manureStorageId
                    );

                    return (
                      <Option
                        disabled={readOnly || isAlreadyUsed}
                        key={`${selectGroupWithIndex}-${dairyManureStorage.id}`}
                        data-testid={`${selectGroupWithIndex}-${dairyManureStorage.id}`}
                        value={dairyManureStorage.name}
                      >
                        {t(`${DAIRY_PRACTICE_SLUG.MANURE_STORAGE}.${dairyManureStorage.translationSlug}`, { ns: "dairy" }) || dairyManureStorage.name}
                      </Option>
                    );
                  })}
                </Select>

                <InputNumber
                  key={`amountStoredPct-${index}-${manure.manureStorageId}`}
                  disabled={readOnly}
                  name={`amountStoredPct-${index}`}
                  dataTestId="footprinting-manure-storage-pct-input"
                  label={t("dairyManureStorageForm.question.manureStoragePctQuestionInput")}
                  suffix="%"
                  value={formatFractionInput(manure.amountStoredPct)}
                  initialValue={formatFractionInput(manure.amountStoredPct)}
                  onChange={(v) => updateItem({ ...manure, amountStoredPct: formatPercentageInput(v) })}
                  validateFirst
                  rules={[
                    { required: true },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                          return Promise.reject(new Error(t("dairyManureStorageForm.error.validPercentage")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Form.Item>
            );
          })}

          <Button
            type="text"
            dataTestId="add-manure-storage-button"
            disabled={readOnly}
            onClick={() => {
              setAppliedManureStorage([...appliedManureStorage, { ...manureStoragePlaceholder }]);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("dairyManureStorageForm.add")}
          </Button>
        </Card>

        <Card className="padding-200 margin-top-100">
          <Flex vertical>
            <Flex vertical gap={8}>
              <h4>{t("dairyManureStorageForm.gasTightStorageTitle")}</h4>
              <p className="margin-top-0 margin-bottom-150 grey-600-text paragraph-m">{t("dairyManureStorageForm.gasTightStorageSubtitle")}</p>
            </Flex>
            <Form.Item>
              <InputNumber
                disabled={readOnly}
                name="amountStoredGasTankPct"
                dataTestId="footprinting-manure-gas-tight-storage-input"
                label={t("dairyManureStorageForm.question.gasTightStorageTypeQuestionInput")}
                suffix="%"
                value={formatFractionInput(amountStoredGasTankPct)}
                initialValue={formatFractionInput(amountStoredGasTankPct)}
                onChange={(v) => setAmountStoredGasTankPct(formatPercentageInput(v))}
                validateFirst
                rules={[
                  { required: true },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                        return Promise.reject(new Error(t("dairyManureStorageForm.error.validPercentage")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
          </Flex>
        </Card>
      </Form>
    </div>
  );
};
